import { useState, useRef, useEffect } from "react";
import {
  AdminContext,
  defaultI18nProvider,
  localStorageStore,
} from "react-admin";
import Keycloak from "keycloak-js";
import {
  keycloakAuthProvider,
  raKeycloakOptions,
} from "./core/keycloakAuthProvider";
import { httpClient } from "./core/httpClient";
import { apiDataProvider } from "./core/apiProvider";
import { Loading } from "./components/customLoading";

import { STORE_VERSION } from "./config/settings";
import { cleanThemeCache, ENV, setEnvFavicon } from "./ra-lb-tools/utils/tools";
import { initialize } from "./ra-lb-tools/config/packageSetup";
import {
  AddToQueue,
  AttachMoney,
  BugReportOutlined,
  CurrencyExchange,
  DisplaySettings,
  Dns,
  MeetingRoom,
  MoreHoriz,
} from "@mui/icons-material";
import { AppUI } from "./AppUI";
import { resetPermissions } from "./ra-lb-tools/acl/access";
import { jiTheme } from "./theme/theme";
import { AuthProxy } from "./core/authProxy";
import {
  customEnvLoginAttemp,
  resetCustomEnvTries,
} from "./ra-lb-tools/utils/customEnv";
import { MyContextProvider } from "./ra-lb-tools/components/myContext";
import { WithAccessControlProvider } from "./ra-lb-tools/acl/withAccessControlProvider";

const App = () => {
  customEnvLoginAttemp();

  initialize({
    menuGroups: [
      { label: "Locker Configuration", sortOrder: 11, icon: Dns },
      { label: "Locker Open", sortOrder: 21, icon: MeetingRoom },
      { label: "billing", sortOrder: 31, icon: AttachMoney },
      { label: "Billing Activity", sortOrder: 41, icon: CurrencyExchange },
      { label: "Server Management", sortOrder: 51, icon: DisplaySettings },
      { label: "Console", sortOrder: 61, icon: AddToQueue },
      { label: "Setups", sortOrder: 81, icon: MoreHoriz },
      { label: "Activity", sortOrder: 91, icon: MoreHoriz },
      { label: "debug", icon: BugReportOutlined, debug: true },
    ],
  });

  setEnvFavicon();

  const [keycloak, setKeycloak] = useState(undefined);
  const keycloakError = useRef(undefined);

  const authProvider = useRef(undefined);
  const dataProvider = useRef(undefined);

  useEffect(() => {
    const initKeyCloakClient = async () => {
      resetPermissions();

      const _ENV = ENV();
      let keycloakClient = null;

      try {
        keycloakClient = new Keycloak(_ENV.KEYCLOAK_CONFIG);
        await keycloakClient.init(_ENV.KEYCLOAK_INIT_OPTIONS);
      } catch (error) {
        keycloakError.current = error.error;
      }

      const AuthClient = AuthProxy(keycloakClient);

      authProvider.current = keycloakAuthProvider(
        AuthClient,
        raKeycloakOptions
      );
      dataProvider.current = apiDataProvider(
        _ENV.API_URL,
        httpClient(AuthClient)
      );
      setKeycloak(AuthClient);
    };

    if (keycloakError.current) return;

    if (!keycloak) {
      initKeyCloakClient();
    }

    cleanThemeCache();
  }, [keycloak]);

  if (!keycloak)
    return (
      <Loading
        loadingPrimary="Loading"
        loadingSecondary="Retrieving auth info"
      />
    );
  resetCustomEnvTries();

  return (
    <MyContextProvider value={{ keycloakError: keycloakError.current }}>
      <AdminContext
        requireAuth={!keycloakError.current}
        authProvider={keycloakError.current ? null : authProvider.current}
        dataProvider={dataProvider.current}
        store={localStorageStore(STORE_VERSION)}
        i18nProvider={defaultI18nProvider}
        theme={jiTheme}
      >
        <WithAccessControlProvider>
          <AppUI />
        </WithAccessControlProvider>
      </AdminContext>
    </MyContextProvider>
  );
};

export default App;
